"use client";

import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import Image from "next/image";
import Icon from "@/components/icons/Icons";
import { FundManager } from "@/type/schemeType";
import { FOLIO_API_URL } from "@/utils/gloableVariables";

export default function FundManagerList({
  managerId,
  showDetails = true,
}: {
  managerId: string;
  showDetails: boolean;
}) {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [managers, setManagers] = useState<FundManager[] | null>(null);
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!ref.current) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      {
        rootMargin: "100px",
        threshold: 0.1,
      },
    );

    observer.observe(ref.current);

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!isVisible || !managerId) return;

    const fetchData = async () => {
      try {
        const res = await fetch(`/api/fundManagers?manager_id=${managerId}`);
        const data = await res.json();
        if (!res.ok) throw new Error("Failed to fetch managers");
        setManagers(data || []);
      } catch (err) {
        console.error("Compare funds fetch failed:", err);
      }
    };

    fetchData();
  }, [isVisible, managerId]);

  const handleToggle = (index: number) => {
    setOpenIndex((prev) => (prev === index ? null : index));
  };

  const formatFundSize = (value: number): string => `₹${Math.round(value).toLocaleString("en-IN")} Cr.`;

  if (!managers) return <div ref={ref} />;

  return (
    <>
      {showDetails ? (
        <div className="mt-5 sm:mt-8 text-sm sm:text-base">
          <p className="font-medium text-primaryBlack text-base sm:text-lg pb-2 border-b mb-5 border-[#AAAAAA]">
            Fund Managers
          </p>
          {managers.map((manager, index) => (
            <FundManagerCard
              key={index}
              manager={manager}
              index={index}
              isOpen={openIndex === index}
              onToggle={handleToggle}
              formatFundSize={formatFundSize}
            />
          ))}
        </div>
      ) : (
        <>
          <div className="mt-8 text-sm sm:text-base">
            <p className="font-semibold text-primaryBlack pb-2 border-b border-[#AAAAAA] mb-3">Fund Managers</p>
            <div className="flex flex-col sm:flex-row sm:flex-wrap items-start sm:items-center gap-3 sm:gap-12">
              {managers.map((manager, index) => (
                <SimpleManagerCard key={index} manager={manager} />
              ))}
            </div>
          </div>
        </>
      )}
    </>
  );
}

function FundManagerCard({
  manager,
  index,
  isOpen,
  onToggle,
  formatFundSize,
}: {
  manager: FundManager;
  index: number;
  isOpen: boolean;
  onToggle: (index: number) => void;
  formatFundSize: (value: number) => string;
}) {
  const [imgSrc, setImgSrc] = useState(() => {
    const formattedName = (manager.fund_manager || "")
      .replace(/\b(?:Mr|Ms|Mrs)\.?\b/gi, "")
      .trim()
      .toLowerCase()
      .replace(/\s+/g, "-");

    return `https://folio.mysiponline.com/app/manager_image/${formattedName}.jpg`;
  });

  return (
    <div className="pb-3 mb-3 sm:pb-5 sm:mb-5 last:mb-0 last:pb-0 border-b border-[#AAAAAA] last:border-none cursor-pointer transition-all">
      <div className="flex items-center gap-1.5" onClick={() => onToggle(index)}>
        <Image
          src={imgSrc}
          alt={`Fund Manager ${manager.fund_manager}-manager-image`}
          width={40}
          height={40}
          className="w-10 h-10 rounded-full object-contain"
          onError={() =>
            setImgSrc(
              "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcQB0TsaR0zZyUTi4PwtM4ttcjGZw8aDVgq4ZQ&usqp=CAU",
            )
          }
        />
        <span className="text-primaryBlack font-medium">{manager.fund_manager}</span>
        <div className="ml-auto transition-transform duration-300">
          <Icon.expandableIcon
            isOpen={isOpen}
            className={`ml-auto ${!isOpen ? "text-primaryBlack" : "text-primaryBlue"}`}
          />
        </div>
      </div>

      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            key="content"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="overflow-hidden text-[#444] mt-3"
          >
            <p>
              {manager.fund_manager} is currently managing{" "}
              <strong>
                {manager.fund_manage} fund{manager.fund_manage > 1 ? "s" : ""}
              </strong>
              , with a total AUM of{" "}
              <strong className="text-primaryBlue">{formatFundSize(Number(manager.fund_size))}</strong>. His strategic
              focus on long-term value has contributed to consistent performance at {manager.amc} Mutual Fund.
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function SimpleManagerCard({ manager }: { manager: FundManager }) {
  const [imgSrc, setImgSrc] = useState(() => {
    const formattedName = (manager.fund_manager || "")
      .replace(/\b(?:Mr|Ms|Mrs)\.?\b/gi, "")
      .trim()
      .toLowerCase()
      .replace(/\s+/g, "-");

    return `https://folio.mysiponline.com/app/manager_image/${formattedName}.jpg`;
  });

  return (
    <p className="flex items-center gap-1.5">
      <Image
        src={imgSrc}
        alt={`Fund Manager ${manager.fund_manager}-manager-image`}
        width={40}
        height={40}
        className="w-10 h-10 border border-gray-200 shadow-sm rounded-full shrink-0 object-contain"
        onError={() =>
          setImgSrc(
            "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcQB0TsaR0zZyUTi4PwtM4ttcjGZw8aDVgq4ZQ&usqp=CAU",
          )
        }
      />
      <span className="text-primaryBlack whitespace-nowrap">{manager.fund_manager}</span>
    </p>
  );
}
